import { Badge, Timeline, resolveTaxRegime } from '@bbpos/ui';
import type { TimelineItem } from '@bbpos/ui';
import type { Tenant } from '@/types/tenant';
import {
  formatINRDate,
  planTone,
  subscriptionDaysRemaining,
  toneClasses,
} from '@/components/features/subscriptions/design-tokens';
import { TenantStatusBadge } from './TenantStatusBadge';
import { formatTenantCountry } from './tenant-ui';

// ─── Design tokens ──────────────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

interface TenantOverviewTabProps {
  tenant: Tenant;
}

function Field({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-[0.15em] text-neutral-400 dark:text-neutral-500">
        {label}
      </dt>
      <dd className={cn(
        'mt-1 text-sm text-neutral-900 dark:text-neutral-100',
        mono && 'font-mono tabular-nums',
      )}>
        {value || '—'}
      </dd>
    </div>
  );
}

function buildTimelineItems(tenant: Tenant): TimelineItem[] {
  const items: TimelineItem[] = [];

  // Build timeline with actual per-event timestamps when available
  if (tenant.status === 'SUSPENDED') {
    items.push({
      id: 'suspended',
      action: 'Tenant suspended',
      description: 'Read-only mode enabled',
      type: 'failed',
      timestamp: tenant.suspended_at ?? tenant.updated_at,
    });
  }
  if (tenant.status === 'CANCELLED' || tenant.status === 'CHURNED') {
    items.push({
      id: 'cancelled',
      action: 'Tenant cancelled',
      description: 'Data lifecycle started (90-day hard delete)',
      type: 'cancelled',
      timestamp: tenant.cancelled_at ?? tenant.updated_at,
    });
  }
  if (['ACTIVE', 'TRIAL', 'SUSPENDED', 'CANCELLED', 'CHURNED'].includes(tenant.status)) {
    items.push({
      id: 'active',
      action: 'Tenant activated',
      description: 'Full access enabled',
      type: 'active',
      timestamp: tenant.activated_at ?? tenant.updated_at,
    });
  }
  if (['ACTIVE', 'TRIAL', 'PROVISIONED', 'SUSPENDED', 'CANCELLED', 'CHURNED'].includes(tenant.status)) {
    items.push({
      id: 'provisioned',
      action: 'Tenant provisioned',
      description: 'Database schema and resources created',
      type: 'approved',
      timestamp: tenant.provisioned_at ?? tenant.updated_at,
    });
  }
  if (['APPROVED', 'PROVISIONED', 'ACTIVE', 'TRIAL', 'SUSPENDED', 'CANCELLED', 'CHURNED'].includes(tenant.status)) {
    items.push({
      id: 'approved',
      action: 'Application approved',
      description: 'Admin approved the tenant application',
      type: 'approved',
      timestamp: tenant.approved_at ?? tenant.created_at,
    });
  }
  items.push({
    id: 'created',
    action: 'Tenant created',
    description: 'Application submitted for approval',
    type: 'pending',
    timestamp: tenant.created_at,
  });

  return items;
}

const SUB_ICON_PATHS = {
  plan: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  calendarStart: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  calendarEnd:
    'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2zM9 16l2 2 4-4',
} as const;

function SubscriptionTile({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: string | number;
  tone: Parameters<typeof toneClasses>[0];
  icon: keyof typeof SUB_ICON_PATHS;
}) {
  const palette = toneClasses(tone);
  return (
    <div className="rounded-xl border border-neutral-100 dark:border-neutral-700 bg-white/70 dark:bg-neutral-800/60 p-4 transition-all duration-200 hover:shadow-md">
      <div className="flex items-center gap-2.5">
        <span className={cn('flex h-8 w-8 items-center justify-center rounded-lg', palette.icon)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d={SUB_ICON_PATHS[icon]} />
          </svg>
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-neutral-400 dark:text-neutral-500">
          {label}
        </span>
      </div>
      <p className="mt-2 text-lg font-semibold tracking-tight text-neutral-900 dark:text-neutral-50 tabular-nums">
        {value}
      </p>
    </div>
  );
}

export function TenantOverviewTab({ tenant }: TenantOverviewTabProps) {
  const timelineItems = buildTimelineItems(tenant);
  // Platform users view an arbitrary tenant here, so the tax-id label follows
  // the *viewed* tenant's immutable currency — not the admin's. Legacy/INR
  // tenants resolve to GST_INDIA → 'GSTIN', byte-identical.
  const tax = resolveTaxRegime(tenant.default_currency);
  // Provisioning starts a trial (trial_ends_at) without a subscription period,
  // so the trial end stands in until a subscription is recorded.
  const endsAt = tenant.subscription_expires_at ?? tenant.trial_ends_at;
  const daysRemaining = subscriptionDaysRemaining(endsAt);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 animate-slide-up">
      {/* Subscription — mirrored from the Subscription tab so platform admins
          see plan + renewal dates without switching tabs. */}
      <section className={cn(GLASS_PANEL, 'lg:col-span-5 p-6 transition-shadow duration-200 hover:shadow-lg')}>
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
          Subscription
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SubscriptionTile
            label="Current Plan"
            value={tenant.subscription_plan ?? '—'}
            tone={planTone(tenant.subscription_plan)}
            icon="plan"
          />
          <SubscriptionTile
            label="Days Remaining"
            value={endsAt ? daysRemaining : '—'}
            tone={daysRemaining > 30 ? 'success' : daysRemaining > 7 ? 'warning' : 'danger'}
            icon="clock"
          />
          <SubscriptionTile
            label="Start Date"
            value={formatINRDate(tenant.subscription_started_at)}
            tone="info"
            icon="calendarStart"
          />
          <SubscriptionTile
            label="End Date"
            value={formatINRDate(endsAt)}
            tone="info"
            icon="calendarEnd"
          />
        </div>
      </section>

      {/* Business Details */}
      <section className={cn(GLASS_PANEL, 'lg:col-span-3 p-6 transition-shadow duration-200 hover:shadow-lg')}>
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
          Business Details
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Business Name" value={tenant.business_name} />
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.15em] text-neutral-400 dark:text-neutral-500">
              Business Type
            </dt>
            <dd className="mt-1 flex items-center gap-2">
              <Badge variant={tenant.business_type === 'RETAIL' ? 'info' : 'success'}>
                {tenant.business_type}
              </Badge>
              <svg className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </dd>
          </div>
          {/* Set once at creation (the wizard's Review step shows the same
              line) and never editable — hence the lock. */}
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.15em] text-neutral-400 dark:text-neutral-500">
              Country / Currency
            </dt>
            <dd className="mt-1 flex items-center gap-2 text-sm text-neutral-900 dark:text-neutral-100">
              {formatTenantCountry(tenant.default_currency)} · {tax.taxName}
              <svg className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </dd>
            <p className="mt-0.5 text-xs text-neutral-400 dark:text-neutral-500">Set at creation · cannot be changed</p>
          </div>
          {/* India stores its GSTIN in `gstin`; GCC tenants store TRN/VATIN in
              `tax_registration_number`. */}
          <Field
            label={tax.taxIdLabel ?? 'Tax ID'}
            value={tax.regime === 'GST_INDIA' ? tenant.gstin : tenant.tax_registration_number}
            mono
          />
          <Field label="Owner Email" value={tenant.owner_email} />
          <Field label="Owner Name" value={tenant.owner_name} />
          <Field label="Phone" value={tenant.phone} mono />
          <div className="sm:col-span-2">
            <Field
              label="Address"
              value={
                [tenant.address, tenant.city, tenant.state, tenant.pincode]
                  .filter(Boolean)
                  .join(', ') || undefined
              }
            />
          </div>
          <Field
            label="Created"
            value={new Date(tenant.created_at).toLocaleDateString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric',
            })}
            mono
          />
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.15em] text-neutral-400 dark:text-neutral-500">
              Status
            </dt>
            <dd className="mt-1">
              <TenantStatusBadge status={tenant.status} />
            </dd>
          </div>
        </dl>
      </section>

      {/* Lifecycle Timeline */}
      <section className={cn(GLASS_PANEL, 'lg:col-span-2 p-6 transition-shadow duration-200 hover:shadow-lg')}>
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
          Lifecycle Timeline
        </h3>
        <Timeline items={timelineItems} variant="default" />
      </section>
    </div>
  );
}
